import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { fetchAPI } from "../../config/api";
import { PLAYER_HEADSHOT_FALLBACK } from "../../config/constants";

const Headshot = ({ src, name }) => (
  <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
    <img 
      src={src || PLAYER_HEADSHOT_FALLBACK} 
      alt={name} 
      className="w-full h-full object-cover object-top"
      onError={(e) => (e.currentTarget.src = PLAYER_HEADSHOT_FALLBACK)}
    />
  </div>
);

const getRoleInfo = (gamesPlayed, totalGames) => {
  const pct = gamesPlayed / totalGames;
  if (pct >= 0.8) return { role: "Everyday", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" };
  if (pct >= 0.5) return { role: "Platoon", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" };
  if (pct >= 0.1) return { role: "Rotation", color: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200" };
  return { role: "Bench", color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200" };
};

const getLineupSpot = (avgOrder) => {
  if (!avgOrder) return null;
  const rounded = Math.round(avgOrder);
  if (rounded <= 2) return { label: "Top", color: "text-amber-600", bg: "bg-amber-50" };
  if (rounded === 3) return { label: "3-Hole", color: "text-rose-600", bg: "bg-rose-50" };
  if (rounded === 4) return { label: "Cleanup", color: "text-red-600", bg: "bg-red-50" };
  if (rounded <= 6) return { label: "Middle", color: "text-blue-600", bg: "bg-blue-50" };
  return { label: "Bottom", color: "text-slate-500", bg: "bg-slate-50" };
};

const InfoTooltip = ({ children }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <Info size={14} />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
          {children}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

const PositionBadge = ({ position }) => {
  if (!position) return null;
  const posColors = {
    'C': 'bg-amber-100 text-amber-700',
    'INF': 'bg-blue-100 text-blue-700',
    '1B': 'bg-blue-100 text-blue-700',
    '2B': 'bg-blue-100 text-blue-700',
    'SS': 'bg-blue-100 text-blue-700',
    '3B': 'bg-blue-100 text-blue-700',
    'OF': 'bg-green-100 text-green-700',
    'LF': 'bg-green-100 text-green-700',
    'CF': 'bg-green-100 text-green-700',
    'RF': 'bg-green-100 text-green-700',
    'DH': 'bg-gray-100 text-gray-700',
    'UT': 'bg-purple-100 text-purple-700',
  };
  const mainPos = position.split('/')[0].split(',')[0].trim().toUpperCase();
  const colorClass = posColors[mainPos] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colorClass}`}>
      {position.length > 6 ? position.substring(0, 6) : position}
    </span>
  );
};

export const LineupUsage = ({ orgId, division, years }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(years?.[0] || null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const yearParam = selectedYear ? `&year=${selectedYear}` : "";
        const response = await fetchAPI(`/api/team/${orgId}/lineup-usage?division=${division}${yearParam}`);
        setData(response);
        if (!selectedYear && response.available_years?.length) {
          setSelectedYear(response.available_years[0]);
        }
      } catch (err) {
        console.error("Error fetching lineup usage:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, division, selectedYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.batters?.length) {
    return <div className="text-center py-12 text-gray-500">No lineup data available</div>;
  }

  const totalGames = data.batters[0]?.total_games || 1;
  const starterThreshold = Math.ceil(totalGames * 0.5);
  
  const starters = data.batters.filter(b => b.games >= starterThreshold);
  const bench = data.batters.filter(b => b.games < starterThreshold && b.games >= 2);
  const availableYears = data.available_years || years || [];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Lineup Usage</h2>
        {availableYears.length > 0 && (
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map((yr) => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
        )}
      </div>

      {starters.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">Starters</h3>
            <InfoTooltip>
              <p>Players in 50%+ of games.</p>
            </InfoTooltip>
          </div>
          <div className="grid gap-2">
            {starters.map((b, i) => {
              const { role, color } = getRoleInfo(b.games, totalGames);
              const lineupSpot = getLineupSpot(b.avg_order);
              return (
                <div
                  key={b.batter_id}
                  onClick={() => navigate(`/player/${b.batter_id}`)}
                  className="flex items-center gap-4 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer group"
                >
                  <span className="text-lg font-bold text-gray-300 w-5">{i + 1}</span>
                  <Headshot src={b.img_url} name={b.player_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                        {b.player_name}
                      </span>
                      <PositionBadge position={b.position} />
                      {lineupSpot && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${lineupSpot.bg} ${lineupSpot.color}`}>
                          {lineupSpot.label}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {b.games} G • {b.pa} PA • <span className={color}>{role}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{b.ba?.toFixed(3).replace('0.', '.') ?? "—"}</div>
                      <div className="text-[10px] text-gray-400">AVG</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-gray-900">{b.hr ?? "—"}</div>
                      <div className="text-[10px] text-gray-400">HR</div>
                    </div>
                    <div className="text-center min-w-[40px]">
                      <div className="font-semibold text-gray-900">{b.war?.toFixed(1) ?? "—"}</div>
                      <div className="text-[10px] text-gray-400">WAR</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {bench.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">Bench</h3>
            <InfoTooltip>
              <p>Players with fewer than 50% of games.</p>
            </InfoTooltip>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {bench.map((b) => {
              const { role, color, bg, border } = getRoleInfo(b.games, totalGames);
              const lineupSpot = getLineupSpot(b.avg_order);
              return (
                <div
                  key={b.batter_id}
                  onClick={() => navigate(`/player/${b.batter_id}`)}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer group ${bg} ${border} hover:shadow-sm`}
                >
                  <Headshot src={b.img_url} name={b.player_name} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate text-sm">
                        {b.player_name}
                      </span>
                      <PositionBadge position={b.position} />
                      {lineupSpot && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${lineupSpot.bg} ${lineupSpot.color}`}>
                          {lineupSpot.label}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {b.games} G • <span className={color}>{role}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-700">
                      {b.ba?.toFixed(3).replace('0.', '.') ?? "—"}
                    </div>
                    <div className="text-[10px] text-gray-400">AVG</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
